import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { session } from '@/lib/api/session';
import { settingsService } from '@/lib/api/services/settings.service';
import { FALLBACK_CURRENCY, setDefaultCurrency } from '@/lib/format';

const CACHE_PREFIX = 'xyra_base_currency:';
const CODE_RE = /^[A-Z]{3}$/;

function readCached(slug: string): string | null {
  try {
    return localStorage.getItem(CACHE_PREFIX + slug);
  } catch {
    return null; // storage disabled (private mode / embedded webview)
  }
}

function writeCached(slug: string, code: string): void {
  try {
    localStorage.setItem(CACHE_PREFIX + slug, code);
  } catch {
    /* non-fatal: the next visit just starts from the fallback until the fetch lands */
  }
}

/**
 * Applies the tenant's Finance "Reporting currency" (`finance.baseCurrency`) as
 * the app-wide default for money formatting — see setDefaultCurrency() in
 * lib/format.ts — and returns the code so the shell can re-key its routed
 * content when it changes (formatMoney isn't reactive; it's read at render).
 *
 * Rides on the same public-settings request as useBranding() (identical query
 * key, so one network call) — that's the one lookup available to every signed-in
 * user, including client/vendor portal accounts that can't read /settings. The
 * last-known value is cached per tenant in localStorage so a returning user's
 * first paint is already in the right currency instead of flashing "$".
 */
export function useTenantCurrency(): string {
  const slug = session.tenantSlug || '';
  const query = useQuery({
    queryKey: ['branding', slug],
    queryFn: () => settingsService.publicBranding(slug),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const fromServer = query.data?.values?.['finance.baseCurrency'];
  const live = typeof fromServer === 'string' && CODE_RE.test(fromServer) ? fromServer : null;
  const cached = slug ? readCached(slug) : null;
  // Not getDefaultCurrency() as the last resort: after a tenant switch that
  // would still hold the previous tenant's currency.
  const currency = live ?? (cached && CODE_RE.test(cached) ? cached : FALLBACK_CURRENCY);

  // Set during render, not in an effect: children format money in this same
  // render pass and must already see the right default. Idempotent.
  setDefaultCurrency(currency);

  useEffect(() => {
    if (live && slug) writeCached(slug, live);
  }, [live, slug]);

  return currency;
}
